import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { MailWarning, AlertTriangle } from "lucide-react";
import PublicHolidaysAdmin from "@/components/PublicHolidaysAdmin";
import DangerZone from "@/components/DangerZone";
import { useAuth } from "@/contexts/AuthContext";

const Row = ({ label, children, hint }) => (
  <div className="grid sm:grid-cols-3 gap-3 items-start py-3 border-t border-border first:border-0">
    <div>
      <div className="text-sm font-medium">{label}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
    <div className="sm:col-span-2">{children}</div>
  </div>
);

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [company, setCompany] = useState(null);
  const [te, setTe] = useState(null);
  const [hol, setHol] = useState(null);
  const [ot, setOt] = useState(null);
  const [sec, setSec] = useState(null);
  const [pen, setPen] = useState(null);
  const [ins, setIns] = useState(null);
  const [leave, setLeave] = useState(null);
  const [shifts, setShifts] = useState(null);
  const [smtp, setSmtp] = useState({ host: "", port: 587, username: "", password: "", from_email: "", from_name: "", use_tls: true });
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (cancelled) return;
        setSettings(data);
        setCompany(data.company);
        setTe(data.time_entry);
        setHol(data.holiday);
        setOt(data.overtime);
        setSec(data.security);
        setPen(data.pension || { employer_rate: 0.015, eligibility_age_min: 18, dd_attribution: "month_of_deduction", dd_day_of_month: 6, tolerance_eur: 1.00 });
        setIns(data.insurance || { provider: "Insurance Provider", employer_rate: 0.015, combined_floor: 100.00, dd_day_of_month: 6, tolerance_eur: 1.00 });
        setLeave(data.leave || {
          holiday_year_start_month: 1, holiday_year_start_day: 1,
          default_full_time_holiday_weeks: 4, default_part_time_method: "part_time_accrual_8pct",
          default_day_off_days: 20,
          enable_carryover: false, carryover_limit_days: 5,
          default_statutory_sick_days: 5, sick_pay_percentage: 70, sick_pay_daily_cap_eur: 110,
          require_medical_cert: true,
          self_service_holiday_requests: true, self_service_sick_requests: true,
        });
        setShifts(data.shift_presets || {
          standard: { label: "Standard Day", time_in: "07:30", time_out: "17:30", break_minutes: 120 },
          early:    { label: "Early",        time_in: "07:00", time_out: "15:00", break_minutes: 30 },
          late:     { label: "Late",         time_in: "13:00", time_out: "21:00", break_minutes: 30 },
        });
        setSmtp((s) => ({ ...s, host: data.smtp.host, port: data.smtp.port, username: data.smtp.username, from_email: data.smtp.from_email, from_name: data.smtp.from_name, use_tls: data.smtp.use_tls }));
      } catch (e) { if (!cancelled) toast.error(formatApiError(e)); }
    })();
    return () => { cancelled = true; };
  }, []);

  const saveCompany = async () => {
    try {
      await api.put("/settings/company", company);
      toast.success("Company saved");
      const { data } = await api.get("/settings");
      setCompany(data.company);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveTe = async () => { try { await api.put("/settings/time-entry", te); toast.success("Policy saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const saveHol = async () => { try { await api.put("/settings/holiday", hol); toast.success("Policy saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const saveOt = async () => { try { await api.put("/settings/overtime", ot); toast.success("Policy saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const saveSec = async () => { try { await api.put("/settings/security", sec); toast.success("Policy saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const savePen = async () => {
    try {
      await api.put("/settings/pension", {
        ...pen,
        employer_rate: Number(pen.employer_rate),
        eligibility_age_min: Number(pen.eligibility_age_min),
        dd_day_of_month: Number(pen.dd_day_of_month),
        tolerance_eur: Number(pen.tolerance_eur),
      });
      toast.success("Pension policy saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveIns = async () => {
    try {
      await api.put("/settings/insurance", {
        ...ins,
        employer_rate: Number(ins.employer_rate),
        combined_floor: Number(ins.combined_floor),
        dd_day_of_month: Number(ins.dd_day_of_month),
        tolerance_eur: Number(ins.tolerance_eur),
      });
      toast.success("Insurance policy saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveLeave = async () => {
    try {
      const payload = {
        ...leave,
        holiday_year_start_month: Number(leave.holiday_year_start_month),
        holiday_year_start_day: Number(leave.holiday_year_start_day),
        default_full_time_holiday_weeks: Number(leave.default_full_time_holiday_weeks),
        default_day_off_days: Number(leave.default_day_off_days ?? 20),
        carryover_limit_days: Number(leave.carryover_limit_days),
        default_statutory_sick_days: Number(leave.default_statutory_sick_days),
        sick_pay_percentage: Number(leave.sick_pay_percentage),
        sick_pay_daily_cap_eur: Number(leave.sick_pay_daily_cap_eur),
      };
      await api.put("/settings/leave", payload);
      toast.success("Leave settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setShift = (key, field, value) => setShifts((s) => ({ ...s, [key]: { ...s[key], [field]: field === "break_minutes" ? Number(value) : value } }));
  const saveShifts = async () => {
    try {
      await api.put("/settings/shift-presets", shifts);
      toast.success("Shift presets saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveSmtp = async () => {
    try {
      const payload = { ...smtp, port: Number(smtp.port) };
      if (!payload.password) delete payload.password;
      await api.put("/settings/smtp", payload);
      toast.success("SMTP saved");
      setSmtp((s) => ({ ...s, password: "" }));
      // Re-fetch settings so smtp.test_email etc. are fresh
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch { /* non-fatal */ }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const testSmtp = async () => {
    if (!testEmail) return toast.error("Enter a recipient email");
    try {
      const { data } = await api.post("/settings/smtp/test", { to_email: testEmail });
      if (data.ok) toast.success("Test email sent");
      else toast.error(`Failed: ${data.message}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!settings) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="tiny-label">Configuration</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Settings</h1>
        <p className="text-muted-foreground mt-1">Company profile, policies, security, and SMTP.</p>
      </div>

      {!settings.smtp.configured && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-800">
          <MailWarning className="w-4 h-4" />
          <AlertDescription>SMTP not configured. Set credentials below to enable email delivery.</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="company" className="space-y-4">
        <TabsList>
          <TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger>
          <TabsTrigger value="time" data-testid="tab-time">Time entry</TabsTrigger>
          <TabsTrigger value="shifts" data-testid="tab-shifts">Shift presets</TabsTrigger>
          <TabsTrigger value="holiday" data-testid="tab-holiday">Holiday</TabsTrigger>
          <TabsTrigger value="public_holidays" data-testid="tab-public-holidays">Public holidays</TabsTrigger>
          <TabsTrigger value="overtime" data-testid="tab-overtime">Overtime</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
          <TabsTrigger value="pension" data-testid="tab-pension">Pension</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance">Insurance</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">Leave</TabsTrigger>
          <TabsTrigger value="smtp" data-testid="tab-smtp">SMTP</TabsTrigger>
          {user?.role === "super_admin" && (
            <TabsTrigger value="danger" data-testid="tab-danger" className="text-red-700 data-[state=active]:text-red-700">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Danger zone
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="company">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            <Row label="Company name"><Input value={company.company_name} onChange={(e) => setCompany({ ...company, company_name: e.target.value })} /></Row>
            <Row label="Country"><Input value={company.country} onChange={(e) => setCompany({ ...company, country: e.target.value })} /></Row>
            <Row label="Timezone"><Input value={company.timezone} onChange={(e) => setCompany({ ...company, timezone: e.target.value })} /></Row>
            <Row label="Week start day" hint="0 = Monday, 6 = Sunday">
              <Select value={String(company.week_start_day)} onValueChange={(v) => setCompany({ ...company, week_start_day: Number(v) })}>
                <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Monday</SelectItem>
                  <SelectItem value="1">Tuesday</SelectItem>
                  <SelectItem value="5">Saturday</SelectItem>
                  <SelectItem value="6">Sunday</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row label="Standard weekly hours"><Input type="number" step="0.5" value={company.standard_weekly_hours} onChange={(e) => setCompany({ ...company, standard_weekly_hours: Number(e.target.value) })} /></Row>
            <Row label="Standard daily hours"><Input type="number" step="0.5" value={company.standard_daily_hours} onChange={(e) => setCompany({ ...company, standard_daily_hours: Number(e.target.value) })} /></Row>
            <div className="mt-4"><Button onClick={saveCompany} data-testid="save-company">Save</Button></div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="time">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            <Row label="Open period" hint="Which period employees can log time for">
              <Select value={te.open_period} onValueChange={(v) => setTe({ ...te, open_period: v })}>
                <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="current_week">Current week only</SelectItem>
                  <SelectItem value="current_month">Current month</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                  <SelectItem value="all">All (no limit)</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            {te.open_period === "custom" && <>
              <Row label="Custom start"><Input type="date" value={te.custom_start || ""} onChange={(e) => setTe({ ...te, custom_start: e.target.value })} /></Row>
              <Row label="Custom end"><Input type="date" value={te.custom_end || ""} onChange={(e) => setTe({ ...te, custom_end: e.target.value })} /></Row>
            </>}
            <Row label="Allow employee self-log"><Switch checked={te.allow_employee_self_log} onCheckedChange={(v) => setTe({ ...te, allow_employee_self_log: v })} data-testid="toggle-self-log" /></Row>
            <div className="mt-4"><Button onClick={saveTe}>Save</Button></div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="holiday">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            <Row label="Default annual leave (days)"><Input type="number" step="0.5" value={hol.default_annual_leave_days} onChange={(e) => setHol({ ...hol, default_annual_leave_days: Number(e.target.value) })} /></Row>
            <Row label="Accrual method" hint="Irish options: fixed annual, 1/3 per month with 117+ hrs, or 8% of hours worked">
              <Select value={hol.accrual_method} onValueChange={(v) => setHol({ ...hol, accrual_method: v })}>
                <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed annual</SelectItem>
                  <SelectItem value="monthly_third">1/3 of week per month (117+ hrs)</SelectItem>
                  <SelectItem value="hours_eight_percent">8% of hours worked</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row label="Allow half days"><Switch checked={hol.allow_half_days} onCheckedChange={(v) => setHol({ ...hol, allow_half_days: v })} /></Row>
            <div className="mt-4"><Button onClick={saveHol}>Save</Button></div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="public_holidays">
          <PublicHolidaysAdmin />
        </TabsContent>

        <TabsContent value="overtime">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            <Row label="Weekly threshold (hours)"><Input type="number" step="0.5" value={ot.threshold_hours_per_week} onChange={(e) => setOt({ ...ot, threshold_hours_per_week: Number(e.target.value) })} /></Row>
            <Row label="Requires approval"><Switch checked={ot.requires_approval} onCheckedChange={(v) => setOt({ ...ot, requires_approval: v })} /></Row>
            <Row label="Premium multiplier"><Input type="number" step="0.1" value={ot.premium_multiplier} onChange={(e) => setOt({ ...ot, premium_multiplier: Number(e.target.value) })} /></Row>
            <div className="mt-4"><Button onClick={saveOt}>Save</Button></div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="security">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            <Row label="Session timeout (minutes)"><Input type="number" value={sec.session_timeout_minutes} onChange={(e) => setSec({ ...sec, session_timeout_minutes: Number(e.target.value) })} /></Row>
            <Row label="Max failed login attempts"><Input type="number" value={sec.max_failed_attempts} onChange={(e) => setSec({ ...sec, max_failed_attempts: Number(e.target.value) })} /></Row>
            <Row label="Lockout duration (minutes)"><Input type="number" value={sec.lockout_minutes} onChange={(e) => setSec({ ...sec, lockout_minutes: Number(e.target.value) })} /></Row>
            <Row label="Require MFA for admins"><Switch checked={sec.require_mfa_for_admins} onCheckedChange={(v) => setSec({ ...sec, require_mfa_for_admins: v })} /></Row>
            <div className="mt-4"><Button onClick={saveSec}>Save</Button></div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="pension">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            {pen && <>
              <Row label="Employer contribution rate (%)" hint="Percentage of gross wage paid by the employer. GBBS internal default is 1.5%. Change here to apply to all standard employees from the next reconciliation run.">
                <Input type="number" step="0.01" min="0" max="100" value={(Number(pen.employer_rate) * 100).toFixed(2)} onChange={(e) => setPen({ ...pen, employer_rate: Number(e.target.value) / 100 })} data-testid="pension-employer-rate" />
              </Row>
              <Row label="Minimum eligibility age" hint="Internal employer policy — employees below this age are NOT auto-enrolled. GBBS internal default is 18. Statutory MyFutureFund rules are tracked separately.">
                <Input type="number" min="0" max="99" value={pen.eligibility_age_min ?? 18} onChange={(e) => setPen({ ...pen, eligibility_age_min: Number(e.target.value) })} data-testid="pension-age-min" />
              </Row>
              <Row label="DD attribution" hint="Zurich takes the direct debit at the start of the next month — choose how to attribute it to the payroll month.">
                <Select value={pen.dd_attribution} onValueChange={(v) => setPen({ ...pen, dd_attribution: v })}>
                  <SelectTrigger className="w-80" data-testid="pension-dd-attribution"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month_of_deduction">Use Zurich &quot;Month of Salary Deduction&quot; (default)</SelectItem>
                    <SelectItem value="dd_date_prior_month">DD date attributes to the previous calendar month</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
              <Row label="Expected DD day-of-month" hint="The day Zurich typically debits (e.g. 6 = the 6th of the following month). 1–28."><Input type="number" min="1" max="28" value={pen.dd_day_of_month} onChange={(e) => setPen({ ...pen, dd_day_of_month: e.target.value })} data-testid="pension-dd-day" /></Row>
              <Row label="Default tolerance (€)" hint="Differences within this many euros are treated as a match. Per-employee variation is reported by month only — multiple weekly payroll entries are aggregated to a single monthly figure."><Input type="number" step="0.01" value={pen.tolerance_eur} onChange={(e) => setPen({ ...pen, tolerance_eur: e.target.value })} data-testid="pension-tolerance" /></Row>
              <div className="mt-4"><Button onClick={savePen} data-testid="save-pension">Save</Button></div>
            </>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="insurance">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            {ins && <>
              <Row label="Provider name" hint="Insurance provider label used in reports."><Input value={ins.provider || ""} onChange={(e) => setIns({ ...ins, provider: e.target.value })} data-testid="insurance-provider" /></Row>
              <Row label="Employer contribution rate (%)" hint="Percentage of gross wage paid by the employer toward insurance.">
                <Input type="number" step="0.01" min="0" max="100" value={(Number(ins.employer_rate) * 100).toFixed(2)} onChange={(e) => setIns({ ...ins, employer_rate: Number(e.target.value) / 100 })} data-testid="insurance-employer-rate" />
              </Row>
              <Row label="Combined monthly floor (€)" hint="Minimum monthly insurance contribution. If the percentage-based calculation falls below this floor, the floor is used and the variance is flagged.">
                <Input type="number" step="0.01" min="0" value={ins.combined_floor} onChange={(e) => setIns({ ...ins, combined_floor: e.target.value })} data-testid="insurance-floor" />
              </Row>
              <Row label="Expected DD day-of-month" hint="The day the insurer typically debits (1–28)."><Input type="number" min="1" max="28" value={ins.dd_day_of_month} onChange={(e) => setIns({ ...ins, dd_day_of_month: e.target.value })} data-testid="insurance-dd-day" /></Row>
              <Row label="Default tolerance (€)" hint="Differences within this many euros are treated as a match."><Input type="number" step="0.01" value={ins.tolerance_eur} onChange={(e) => setIns({ ...ins, tolerance_eur: e.target.value })} data-testid="insurance-tolerance" /></Row>
              <div className="mt-4"><Button onClick={saveIns} data-testid="save-insurance">Save</Button></div>
            </>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="leave">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            {leave && <>
              <Row label="Holiday year start" hint="Most Irish employers run a calendar-year leave year (Jan 1).">
                <div className="flex gap-2">
                  <Select value={String(leave.holiday_year_start_month)} onValueChange={(v) => setLeave({ ...leave, holiday_year_start_month: Number(v) })}>
                    <SelectTrigger className="w-44" data-testid="leave-year-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                        <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" min="1" max="28" className="w-24" value={leave.holiday_year_start_day} onChange={(e) => setLeave({ ...leave, holiday_year_start_day: e.target.value })} data-testid="leave-year-day" />
                </div>
              </Row>
              <Row label="Default full-time holiday (working weeks)" hint="Irish statutory minimum is 4 weeks. 5 days/week → 20 days; 4 days/week → 16 days.">
                <Input type="number" step="0.5" value={leave.default_full_time_holiday_weeks} onChange={(e) => setLeave({ ...leave, default_full_time_holiday_weeks: e.target.value })} data-testid="leave-ft-weeks" />
              </Row>
              <Row label="Default Day Off allowance (days/year)" hint="Additional earned entitlement, separate from annual leave. Accrues monthly (allowance ÷ 12 per completed month). Can be overridden per employee.">
                <Input type="number" step="0.5" min="0" max="60" value={leave.default_day_off_days ?? 20} onChange={(e) => setLeave({ ...leave, default_day_off_days: e.target.value })} data-testid="leave-dayoff-days" />
              </Row>
              <Row label="Default part-time calculation method" hint="Part-time accrual uses 8% of approved hours worked, capped at 4 working weeks.">
                <Select value={leave.default_part_time_method} onValueChange={(v) => setLeave({ ...leave, default_part_time_method: v })}>
                  <SelectTrigger className="w-96" data-testid="leave-pt-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="part_time_pro_rata">Pro-rata of contracted hours</SelectItem>
                    <SelectItem value="part_time_accrual_8pct">8% accrual of hours worked (recommended)</SelectItem>
                    <SelectItem value="manual_override">Manual override</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
              <Row label="Enable carryover">
                <Switch checked={leave.enable_carryover} onCheckedChange={(v) => setLeave({ ...leave, enable_carryover: v })} data-testid="leave-carryover" />
              </Row>
              {leave.enable_carryover && (
                <Row label="Carryover limit (days)">
                  <Input type="number" step="0.5" value={leave.carryover_limit_days} onChange={(e) => setLeave({ ...leave, carryover_limit_days: e.target.value })} data-testid="leave-carryover-limit" />
                </Row>
              )}
              <Row label="Default statutory sick leave (days)" hint="Irish 2026 statutory: 5 days. Employer schemes may be more generous.">
                <Input type="number" step="0.5" value={leave.default_statutory_sick_days} onChange={(e) => setLeave({ ...leave, default_statutory_sick_days: e.target.value })} data-testid="leave-sick-days" />
              </Row>
              <Row label="Sick pay percentage" hint="Statutory: 70% of normal pay.">
                <Input type="number" step="1" min="0" max="100" value={leave.sick_pay_percentage} onChange={(e) => setLeave({ ...leave, sick_pay_percentage: e.target.value })} data-testid="leave-sick-pct" />
              </Row>
              <Row label="Daily sick-pay cap (€)" hint="Statutory: €110/day in 2026.">
                <Input type="number" step="1" value={leave.sick_pay_daily_cap_eur} onChange={(e) => setLeave({ ...leave, sick_pay_daily_cap_eur: e.target.value })} data-testid="leave-sick-cap" />
              </Row>
              <Row label="Require medical certificate"><Switch checked={leave.require_medical_cert} onCheckedChange={(v) => setLeave({ ...leave, require_medical_cert: v })} data-testid="leave-cert-required" /></Row>
              <Row label="Self-service holiday requests"><Switch checked={leave.self_service_holiday_requests} onCheckedChange={(v) => setLeave({ ...leave, self_service_holiday_requests: v })} data-testid="leave-ss-holiday" /></Row>
              <Row label="Self-service sick leave requests"><Switch checked={leave.self_service_sick_requests} onCheckedChange={(v) => setLeave({ ...leave, self_service_sick_requests: v })} data-testid="leave-ss-sick" /></Row>
              <div className="mt-4"><Button onClick={saveLeave} data-testid="save-leave">Save</Button></div>
            </>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="shifts">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            {shifts && <>
              <p className="text-sm text-muted-foreground mb-4">
                These three presets appear on the <strong>Quick staff hours</strong> and <strong>Time entry</strong> pages. Managers tap a preset to fill Start/Finish/Break in one click.
              </p>
              {["standard", "early", "late"].map((k) => (
                <div key={k} className="grid sm:grid-cols-12 gap-3 items-end py-3 border-b border-border last:border-0">
                  <div className="sm:col-span-1 text-xs uppercase tracking-wide text-muted-foreground pb-3 font-semibold">{k}</div>
                  <div className="sm:col-span-4">
                    <Label>Button label</Label>
                    <Input value={shifts[k].label} onChange={(e) => setShift(k, "label", e.target.value)} data-testid={`shift-${k}-label`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Start</Label>
                    <TimeInput value={shifts[k].time_in} onChange={(e) => setShift(k, "time_in", e.target.value)} data-testid={`shift-${k}-in`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Finish</Label>
                    <TimeInput value={shifts[k].time_out} onChange={(e) => setShift(k, "time_out", e.target.value)} data-testid={`shift-${k}-out`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Break (min)</Label>
                    <Input type="number" min="0" max="240" value={shifts[k].break_minutes} onChange={(e) => setShift(k, "break_minutes", e.target.value)} data-testid={`shift-${k}-break`} />
                  </div>
                  <div className="sm:col-span-1 text-right text-xs text-muted-foreground font-mono pb-3">
                    {(() => {
                      const [hi, mi] = shifts[k].time_in.split(":").map(Number);
                      const [ho, mo] = shifts[k].time_out.split(":").map(Number);
                      const h = Math.max(0, ((ho*60+mo) - (hi*60+mi) - Number(shifts[k].break_minutes||0)) / 60);
                      return `${h.toFixed(2)}h`;
                    })()}
                  </div>
                </div>
              ))}
              <div className="mt-4"><Button onClick={saveShifts} data-testid="save-shifts">Save shift presets</Button></div>
            </>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="smtp">
          <Card className="border-border shadow-none"><CardContent className="p-6">
            <Row label="Host"><Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.example.com" data-testid="smtp-host" /></Row>
            <Row label="Port"><Input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: e.target.value })} data-testid="smtp-port" /></Row>
            <Row label="Username"><Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} data-testid="smtp-username" /></Row>
            <Row label="Password" hint={settings.smtp.has_password ? "Password is already set. Enter a new value to replace it." : "Not set"}>
              <Input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} data-testid="smtp-password" />
            </Row>
            <Row label="From email"><Input type="email" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} data-testid="smtp-from-email" /></Row>
            <Row label="From name"><Input value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} /></Row>
            <Row label="Use TLS"><Switch checked={smtp.use_tls} onCheckedChange={(v) => setSmtp({ ...smtp, use_tls: v })} /></Row>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={saveSmtp} data-testid="smtp-save">Save SMTP</Button>
              <Input placeholder="Send test to…" className="w-64" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
              <Button variant="outline" onClick={testSmtp} data-testid="smtp-test">Send test</Button>
            </div>
          </CardContent></Card>
        </TabsContent>

        {user?.role === "super_admin" && (
          <TabsContent value="danger">
            <DangerZone />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
